import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {
    Accordion,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import * as colors from "components/colors/Colors";
import LetterTextEditor from "./LetterTextEditor";
import {
    AI_CONTENT_LOCK_HANDLERS,
    AiHintRow,
    AiHintRowFromSchema,
    asArray,
    asText,
    ClinicalReportSummaryChip,
    comparisonLetterTextKey,
    COMPARISON_DIRECTION_CHIP_COLORS,
    COMPLETENESS_CHIP_COLORS,
    CONFIDENCE_CHIP_COLORS,
    CRITICAL_CHIP_COLORS,
    FINDING_ASSESSMENT_CHIP_COLORS,
    findingLetterTextKey,
    formatSchemaDate,
    KEY_VALUE_ASSESSMENT_DOT_COLORS,
    LockedAiHintsBox,
    SchemaPointList,
    SlimAccordionSummary,
    sortHintsByPriority,
} from "./ClinicalReportSummaryParts";

const COMPARISON_POINT_FIELDS = [
    {schemaField: "c1ClinicallyRelevantChanges", titleKey: "clinicalReportSummarizer.details.comparisonClinicallyRelevantChanges"},
    {schemaField: "c3FindingsNeedingClarification", titleKey: "clinicalReportSummarizer.details.comparisonPointsToClarify"},
    {schemaField: "c4EvidenceBasedRecommendations", titleKey: "clinicalReportSummarizer.details.comparisonRecommendations"},
];

const DETAILED_ANALYSIS_POINT_FIELDS = [
    {schemaField: "gapAnalysis", titleKey: "clinicalReportSummarizer.details.gapAnalysis"},
    {schemaField: "inconsistencies", titleKey: "clinicalReportSummarizer.details.inconsistencies"},
    {schemaField: "followUpRecommendations", titleKey: "clinicalReportSummarizer.details.followUpRecommendations"},
];

const useStyles = makeStyles((theme) => ({
    card: {
        flexDirection: "column",
        background: colors.htmlWhite,
        border: `1px solid ${colors.clinicalOutline}`,
        borderRadius: 10,
        overflow: "hidden",
    },
    cardHead: {
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
    },
    cardTitle: {
        fontWeight: 700,
    },
    cardSubtitle: {
        fontSize: "0.8rem",
        color: colors.clinicalMutedText,
    },
    chipRow: {
        gap: theme.spacing(0.5),
        flexWrap: "wrap",
        justifyContent: "flex-end",
    },
    detailsAccordion: {
        boxShadow: "none",
        borderTop: `1px solid ${colors.clinicalOutline}`,
        "&:before": {display: "none"},
        "&.Mui-expanded": {margin: 0},
    },
    detailsContent: {
        flexDirection: "column",
        gap: theme.spacing(2),
        width: "100%",
        userSelect: "none",
    },
    detailsHeading: {
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: colors.clinicalMutedText,
        marginBottom: theme.spacing(0.5),
    },
    keyValueTable: {
        "& td, & th": {
            fontSize: "0.8rem",
            padding: theme.spacing(0.5, 1),
        },
    },
    keyValueDot: {
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        marginRight: theme.spacing(1),
    },
    detailsList: {
        margin: 0,
        paddingLeft: theme.spacing(2.5),
        fontSize: "0.875rem",
    },
    detailsMutedText: {
        color: colors.clinicalMutedText,
        fontSize: "0.8rem",
    },
}));

const ClinicalReportFindingCard = ({finding, findingIndex, editedLetterTexts, onLetterTextChange, onLetterTextReset, onLetterTextCopy, isLetterTextCopyConfirmed}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();

    const letterTextKey = findingLetterTextKey(finding, findingIndex);
    const findingComparisonLetterTextKey = comparisonLetterTextKey(letterTextKey);
    const findingTitle = asText(finding.title) || asText(finding.findingType);
    const findingSubtitle = [formatSchemaDate(finding.examinationDate), asText(finding.deviceMethod)].filter(Boolean).join(" · ");
    const completenessStatus = finding.completeness?.status;
    const criticalFindings = asArray(finding.criticalFindings).map(asText).filter(Boolean);
    const sortedAiHints = sortHintsByPriority(finding.physicianHints);
    const comparison = finding.comparison && typeof finding.comparison === "object" ? finding.comparison : null;
    const comparisonAiLetterText = asText(comparison?.letterTextAddition);

    return (
        <Flex container className={classes.card}>
            <Flex container className={classes.cardHead}>
                <Flex container direction={"column"}>
                    <Typography variant={"h6"} className={classes.cardTitle}>{findingTitle}</Typography>
                    {findingSubtitle && <Typography className={classes.cardSubtitle}>{findingSubtitle}</Typography>}
                </Flex>
                <Flex container className={classes.chipRow}>
                    <ClinicalReportSummaryChip
                        labelPrefix={"clinicalReportSummarizer.findingAssessment"}
                        schemaValue={finding.assessment}
                        chipColorsBySchemaValue={FINDING_ASSESSMENT_CHIP_COLORS}
                    />
                    <ClinicalReportSummaryChip
                        labelPrefix={"clinicalReportSummarizer.completeness"}
                        schemaValue={completenessStatus}
                        chipColorsBySchemaValue={COMPLETENESS_CHIP_COLORS}
                    />
                </Flex>
            </Flex>

            <LetterTextEditor
                label={translate("clinicalReportSummarizer.letterText.label")}
                aiLetterText={finding.letterText}
                editedLetterText={editedLetterTexts[letterTextKey]}
                onLetterTextChange={(letterText) => onLetterTextChange(letterTextKey, letterText)}
                onLetterTextReset={() => onLetterTextReset(letterTextKey)}
                onLetterTextCopy={onLetterTextCopy}
                isLetterTextCopyConfirmed={isLetterTextCopyConfirmed}
            />

            {comparisonAiLetterText && (
                <LetterTextEditor
                    label={translate("clinicalReportSummarizer.letterText.comparisonLabel")}
                    aiLetterText={comparisonAiLetterText}
                    editedLetterText={editedLetterTexts[findingComparisonLetterTextKey]}
                    onLetterTextChange={(letterText) => onLetterTextChange(findingComparisonLetterTextKey, letterText)}
                    onLetterTextReset={() => onLetterTextReset(findingComparisonLetterTextKey)}
                    onLetterTextCopy={onLetterTextCopy}
                    isLetterTextCopyConfirmed={isLetterTextCopyConfirmed}
                />
            )}

            <LockedAiHintsBox hasHints={criticalFindings.length > 0 || sortedAiHints.length > 0}>
                {criticalFindings.length > 0 && (
                    <AiHintRow
                        chip={<ClinicalReportSummaryChip label={translate("clinicalReportSummarizer.hintPriority.CRITICAL")} chipColors={CRITICAL_CHIP_COLORS} />}
                        title={translate("clinicalReportSummarizer.aiHints.criticalFindings")}
                        text={criticalFindings.join(" · ")}
                    />
                )}
                {sortedAiHints.map((aiHint, aiHintIndex) => (
                    <AiHintRowFromSchema key={aiHintIndex} aiHint={aiHint} />
                ))}
            </LockedAiHintsBox>

            <FindingDetails finding={finding} comparison={comparison} />
        </Flex>
    );
};

const FindingDetails = ({finding, comparison}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();

    const keyValues = asArray(finding.keyValues).filter((keyValue) => keyValue && typeof keyValue === "object");
    const graphicEvaluations = asArray(finding.graphicEvaluations).filter((graphicEvaluation) => graphicEvaluation && typeof graphicEvaluation === "object");
    const detailedAnalysis = finding.detailedAnalysis && typeof finding.detailedAnalysis === "object" ? finding.detailedAnalysis : null;
    const detailedAnalysisPointSections = DETAILED_ANALYSIS_POINT_FIELDS
        .filter((detailedAnalysisPointField) => asArray(detailedAnalysis?.[detailedAnalysisPointField.schemaField]).length > 0);
    const interactionCheck = detailedAnalysis?.interactions && typeof detailedAnalysis.interactions === "object"
        ? detailedAnalysis.interactions
        : null;
    const comparisonPointSections = COMPARISON_POINT_FIELDS
        .filter((comparisonPointField) => asArray(comparison?.[comparisonPointField.schemaField]).length > 0);
    const comparisonDirections = asArray(comparison?.c2ImprovementsDeteriorations)
        .filter((comparisonDirection) => comparisonDirection && typeof comparisonDirection === "object");

    const hasDetails = keyValues.length > 0 || graphicEvaluations.length > 0 || detailedAnalysisPointSections.length > 0
        || !!interactionCheck || comparisonPointSections.length > 0 || comparisonDirections.length > 0;
    if (!hasDetails) {
        return null;
    }

    return (
        <Accordion className={classes.detailsAccordion} TransitionProps={{unmountOnExit: true}}>
            <SlimAccordionSummary title={translate("clinicalReportSummarizer.details.title")} />
            <AccordionDetails>
                <Flex container className={classes.detailsContent} {...AI_CONTENT_LOCK_HANDLERS}>
                    {keyValues.length > 0 && (
                        <div>
                            <Typography className={classes.detailsHeading}>{translate("clinicalReportSummarizer.details.keyValues")}</Typography>
                            <Table size={"small"} className={classes.keyValueTable}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate("clinicalReportSummarizer.details.keyValueParameter")}</TableCell>
                                        <TableCell>{translate("clinicalReportSummarizer.details.keyValueMeasurement")}</TableCell>
                                        <TableCell>{translate("clinicalReportSummarizer.details.keyValueReference")}</TableCell>
                                        <TableCell>{translate("clinicalReportSummarizer.details.keyValueSource")}</TableCell>
                                        <TableCell>{translate("clinicalReportSummarizer.details.keyValueComment")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {keyValues.map((keyValue, keyValueIndex) => (
                                        <TableRow key={keyValueIndex}>
                                            <TableCell>
                                                <span
                                                    className={classes.keyValueDot}
                                                    style={{background: KEY_VALUE_ASSESSMENT_DOT_COLORS[keyValue.assessment] ?? colors.clinicalOutline}}
                                                />
                                                {asText(keyValue.parameter)}
                                            </TableCell>
                                            <TableCell>
                                                {[asText(keyValue.valueAsWritten) || asText(keyValue.numericValue), asText(keyValue.unit)].filter(Boolean).join(" ")}
                                            </TableCell>
                                            <TableCell>{asText(keyValue.referenceRange)}</TableCell>
                                            <TableCell>
                                                {asText(keyValue.source) && translate(`clinicalReportSummarizer.keyValueSource.${asText(keyValue.source)}`, {defaultValue: asText(keyValue.source)})}
                                            </TableCell>
                                            <TableCell>{asText(keyValue.comment)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}

                    {graphicEvaluations.length > 0 && (
                        <div>
                            <Typography className={classes.detailsHeading}>{translate("clinicalReportSummarizer.details.graphicEvaluation")}</Typography>
                            <ul className={classes.detailsList}>
                                {graphicEvaluations.map((graphicEvaluation, graphicEvaluationIndex) => (
                                    <li key={graphicEvaluationIndex}>
                                        <b>{asText(graphicEvaluation.graphic)}:</b> {asText(graphicEvaluation.observation)}{" "}
                                        <ClinicalReportSummaryChip
                                            labelPrefix={"clinicalReportSummarizer.confidence"}
                                            schemaValue={graphicEvaluation.confidence}
                                            chipColorsBySchemaValue={CONFIDENCE_CHIP_COLORS}
                                        />
                                        {asText(graphicEvaluation.deviationFromDeviceInterpretation) && (
                                            <div className={classes.detailsMutedText}>
                                                {translate("clinicalReportSummarizer.details.deviceDeviation")}{" "}
                                                {asText(graphicEvaluation.deviationFromDeviceInterpretation)}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {detailedAnalysisPointSections.map((detailedAnalysisPointField) => (
                        <div key={detailedAnalysisPointField.schemaField}>
                            <Typography className={classes.detailsHeading}>{translate(detailedAnalysisPointField.titleKey)}</Typography>
                            <SchemaPointList schemaPoints={detailedAnalysis[detailedAnalysisPointField.schemaField]} />
                        </div>
                    ))}

                    {interactionCheck && (
                        <div>
                            <Typography className={classes.detailsHeading}>{translate("clinicalReportSummarizer.details.interactions")}</Typography>
                            {interactionCheck.status === "NOT_CHECKABLE" ? (
                                <Typography className={classes.detailsMutedText}>
                                    {translate("clinicalReportSummarizer.details.interactionsNotCheckable")}{" "}
                                    {asText(interactionCheck.missingInformation)}
                                </Typography>
                            ) : (
                                <>
                                    <Typography className={classes.detailsMutedText}>
                                        {translate("clinicalReportSummarizer.details.interactionsChecked")}
                                    </Typography>
                                    <SchemaPointList schemaPoints={interactionCheck.findings} />
                                </>
                            )}
                        </div>
                    )}

                    {comparisonPointSections.map((comparisonPointField) => (
                        <div key={comparisonPointField.schemaField}>
                            <Typography className={classes.detailsHeading}>{translate(comparisonPointField.titleKey)}</Typography>
                            <SchemaPointList schemaPoints={comparison[comparisonPointField.schemaField]} />
                        </div>
                    ))}

                    {comparisonDirections.length > 0 && (
                        <div>
                            <Typography className={classes.detailsHeading}>{translate("clinicalReportSummarizer.details.comparisonDirections")}</Typography>
                            <ul className={classes.detailsList}>
                                {comparisonDirections.map((comparisonDirection, comparisonDirectionIndex) => (
                                    <li key={comparisonDirectionIndex}>
                                        <b>{asText(comparisonDirection.parameter)}:</b>{" "}
                                        {[asText(comparisonDirection.previous), asText(comparisonDirection.current)].filter(Boolean).join(" → ")}{" "}
                                        <ClinicalReportSummaryChip
                                            labelPrefix={"clinicalReportSummarizer.comparisonDirection"}
                                            schemaValue={comparisonDirection.direction}
                                            chipColorsBySchemaValue={COMPARISON_DIRECTION_CHIP_COLORS}
                                        />
                                        {asText(comparisonDirection.comment) && (
                                            <div className={classes.detailsMutedText}>{asText(comparisonDirection.comment)}</div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Flex>
            </AccordionDetails>
        </Accordion>
    );
};

export default ClinicalReportFindingCard;
